import { useCallback, useEffect, useRef, useState } from 'react'
import { fitThesisApi, getSessionToken } from '@/lib/api/client'
import type {
  FitThesisChatEvent,
  FitThesisChatMessage,
  FitThesisBlockKey,
  ThesisConfidence,
} from '@ispot/shared'

const kickedThesisIds = new Set<string>()

/**
 * Chat state for the Hermes-led drawer.
 * Streams SSE events from POST /fit-theses/:id/chat and updates:
 *  - `messages` — full transcript (including the streaming assistant turn)
 *  - `streaming` — true while a turn is in flight
 *  - `lastBlockSaved` — emits each tool call so the UI can flip block state
 *  - `complete` — coach fired mark_complete
 */
export function useFitThesisChat(accountId: string, projectId: string, thesisId: string | null) {
  const [messages, setMessages] = useState<FitThesisChatMessage[]>([])
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastBlockSaved, setLastBlockSaved] = useState<{
    blockKey: FitThesisBlockKey
    value: unknown
    confidence: ThesisConfidence
    at: number
  } | null>(null)
  const [complete, setComplete] = useState(false)

  const streamRef = useRef<AbortController | null>(null)
  const lastUserMessageRef = useRef<string | null>(null)

  // Load prior transcript
  const reload = useCallback(async () => {
    if (!thesisId) { setMessages([]); return }
    try {
      const { messages } = await fitThesisApi.listChatMessages(accountId, projectId, thesisId) as { messages: FitThesisChatMessage[] }
      setMessages(messages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages')
    }
  }, [accountId, projectId, thesisId])

  useEffect(() => { setComplete(false); reload() }, [reload])

  const sendMessage = useCallback(async (userMessage: string): Promise<void> => {
    if (!thesisId || streaming) return
    setError(null)
    lastUserMessageRef.current = userMessage

    // Empty userMessage = proactive kick — don't append a user bubble, just the assistant placeholder.
    const isKick = userMessage.trim().length === 0
    if (isKick) {
      if (kickedThesisIds.has(thesisId)) return
      kickedThesisIds.add(thesisId)
    }

    const assistantId = 'tmp-a-' + Date.now()
    const assistantMsg: FitThesisChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      metadata: { streaming: true },
      createdAt: new Date().toISOString(),
    }

    if (isKick) {
      setMessages(prev => [...prev, assistantMsg])
    } else {
      const userMsg: FitThesisChatMessage = {
        id: 'tmp-u-' + Date.now(),
        role: 'user',
        content: userMessage,
        metadata: {},
        createdAt: new Date().toISOString(),
      }
      setMessages(prev => [...prev, userMsg, assistantMsg])
    }
    setStreaming(true)

    const controller = new AbortController()
    streamRef.current = controller

    try {
      const token = getSessionToken()
      const res = await fetch(fitThesisApi.chatStreamUrl(accountId, projectId, thesisId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ userMessage }),
        signal: controller.signal,
      })

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '')
        throw new Error(`Chat stream failed: ${res.status} ${text}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Parse SSE frames — each event terminated by blank line
        const frames = buffer.split('\n\n')
        buffer = frames.pop() ?? ''
        for (const frame of frames) {
          const line = frame.split('\n').find(l => l.startsWith('data:'))
          if (!line) continue
          try {
            const evt: FitThesisChatEvent = JSON.parse(line.slice(5).trim())
            handleEvent(evt, assistantId)
          } catch (parseErr) {
            // skip malformed frames
          }
        }
      }
    } catch (err) {
      if ((err as any)?.name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Stream failed')
        if (isKick) kickedThesisIds.delete(thesisId)
        // Drop the partial assistant placeholder so the retry starts clean.
        setMessages(prev => prev.filter(m => m.id !== assistantId))
      }
    } finally {
      setStreaming(false)
      streamRef.current = null
    }

    function handleEvent(evt: FitThesisChatEvent, assistantMessageId: string) {
      switch (evt.kind) {
        case 'assistant-delta':
          setMessages(prev => prev.map(m => m.id === assistantMessageId
            ? { ...m, content: m.content + evt.text }
            : m))
          break
        case 'assistant-complete':
          setMessages(prev => {
            // Suppress empty assistant bubbles — happens when the turn fires tool
            // calls without any prose. The block-saved event already rendered the
            // meaningful outcome on the right panel.
            if (!evt.content || evt.content.trim().length === 0) {
              return prev.filter(m => m.id !== assistantMessageId)
            }
            return prev.map(m => m.id === assistantMessageId
              ? { ...m, content: evt.content, metadata: { ...m.metadata, streaming: false } }
              : m)
          })
          break
        case 'block-saved':
          setLastBlockSaved({ blockKey: evt.blockKey, value: evt.value, confidence: evt.confidence, at: Date.now() })
          break
        case 'complete':
          setComplete(true)
          break
        case 'error':
          setError(evt.message)
          break
      }
    }
  }, [accountId, projectId, thesisId, streaming])

  const cancel = useCallback(() => {
    streamRef.current?.abort()
    streamRef.current = null
    setStreaming(false)
  }, [])

  /**
   * Re-send the last user message. Uses the ref so the UI doesn't need to
   * remember state across the error. If nothing was sent yet, resolves without
   * doing anything.
   */
  const retry = useCallback(async (): Promise<void> => {
    if (lastUserMessageRef.current === null || streaming) return
    const msg = lastUserMessageRef.current
    // Kick-retry: let the kick fire again for this thesis.
    if (msg.trim().length === 0 && thesisId) kickedThesisIds.delete(thesisId)
    setError(null)
    await sendMessage(msg)
  }, [sendMessage, streaming, thesisId])

  return {
    messages,
    streaming,
    error,
    lastBlockSaved,
    complete,
    sendMessage,
    retry,
    reload,
    cancel,
  }
}
